import { useState } from 'react';
import type { DutchVerb } from '@homeapp/shared';

interface Props {
  onGenerate: (infinitive: string) => Promise<DutchVerb | null>;
  onSave: (verb: Partial<DutchVerb>) => Promise<DutchVerb | null>;
  loading?: boolean;
}

export default function VerbEntryTool({ onGenerate, onSave, loading }: Props) {
  const [infinitive, setInfinitive] = useState('');
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generated, setGenerated] = useState<Partial<DutchVerb> | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGenerate = async () => {
    if (!infinitive.trim()) return;
    setGenerating(true);
    setError(null);
    setSaved(false);
    try {
      const verb = await onGenerate(infinitive.trim());
      if (verb) {
        setGenerated(verb);
      } else {
        setError('Failed to generate verb data');
      }
    } catch {
      setError('Failed to generate verb data');
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!generated) return;
    setSaving(true);
    setError(null);
    try {
      const result = await onSave(generated);
      if (result) {
        setSaved(true);
        setGenerated(null);
        setInfinitive('');
      } else {
        setError('Failed to save verb');
      }
    } catch {
      setError('Failed to save verb');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (path: string, value: unknown) => {
    if (!generated) return;
    const keys = path.split('.');
    const updated = { ...generated };
    let obj: Record<string, unknown> = updated;
    for (let i = 0; i < keys.length - 1; i++) {
      obj[keys[i]] = { ...(obj[keys[i]] as Record<string, unknown>) };
      obj = obj[keys[i]] as Record<string, unknown>;
    }
    obj[keys[keys.length - 1]] = value;
    setGenerated(updated);
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-gray-900">Add New Verb</h3>

      {/* Input + Generate */}
      <div className="flex gap-2">
        <input
          type="text"
          value={infinitive}
          onChange={(e) => setInfinitive(e.target.value)}
          placeholder="Enter infinitive (e.g., aandoen)"
          className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          onKeyDown={(e) => e.key === 'Enter' && handleGenerate()}
        />
        <button
          onClick={handleGenerate}
          disabled={generating || !infinitive.trim()}
          className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors whitespace-nowrap"
        >
          {generating ? '⏳ Generating...' : '🤖 Generate'}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">{error}</div>
      )}

      {saved && (
        <div className="bg-green-50 text-green-700 p-3 rounded-lg text-sm">
          ✅ Verb saved successfully!
        </div>
      )}

      {/* Generated verb review/edit form */}
      {generated && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-gray-900">Review & Edit</h4>
            <div className="flex gap-2">
              <button
                onClick={() => setGenerated(null)}
                className="text-gray-500 hover:text-gray-700 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="bg-green-600 text-white px-4 py-1.5 rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm transition-colors"
              >
                {saving ? 'Saving...' : '💾 Save to DB'}
              </button>
            </div>
          </div>

          {/* Basic info */}
          <div className="grid grid-cols-2 gap-3">
            <EditField label="Infinitive" value={generated.infinitive || ''} onChange={(v) => updateField('infinitive', v)} />
            <EditField label="English" value={generated.english || ''} onChange={(v) => updateField('english', v)} />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Separable</label>
              <input type="checkbox" checked={generated.separable || false} onChange={(e) => updateField('separable', e.target.checked)} />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Irregular</label>
              <input type="checkbox" checked={generated.irregular || false} onChange={(e) => updateField('irregular', e.target.checked)} />
            </div>
            <div>
              <label className="text-sm text-gray-600">Difficulty</label>
              <select
                value={generated.difficulty || 3}
                onChange={(e) => updateField('difficulty', Number(e.target.value))}
                className="ml-2 border rounded px-2 py-1 text-sm"
              >
                {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </div>

          {generated.separable && (
            <EditField label="Prefix" value={generated.prefix || ''} onChange={(v) => updateField('prefix', v)} />
          )}

          {/* Present tense conjugations */}
          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">Present Tense (Tegenwoordige tijd)</div>
            <div className="grid grid-cols-2 gap-2">
              {(['ik', 'jij', 'hij', 'wij', 'jullie', 'zij'] as const).map(person => (
                <EditField
                  key={person}
                  label={person}
                  value={generated.present?.[person] || ''}
                  onChange={(v) => updateField(`present.${person}`, v)}
                />
              ))}
            </div>
          </div>

          {/* Past tenses */}
          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">Verleden Tijd & Voltooid Deelwoord</div>
            <div className="grid grid-cols-1 gap-2">
              <EditField label="Verleden enkelvoud" value={generated.pastSingular || ''} onChange={(v) => updateField('pastSingular', v)} />
              <EditField label="Verleden meervoud" value={generated.pastPlural || ''} onChange={(v) => updateField('pastPlural', v)} />
              <EditField label="Voltooid deelwoord" value={generated.pastParticiple || ''} onChange={(v) => updateField('pastParticiple', v)} />
              <div>
                <label className="text-sm text-gray-600">Auxiliary</label>
                <select
                  value={generated.auxiliary || 'hebben'}
                  onChange={(e) => updateField('auxiliary', e.target.value)}
                  className="ml-2 border rounded px-2 py-1 text-sm"
                >
                  <option value="hebben">hebben</option>
                  <option value="zijn">zijn</option>
                </select>
              </div>
            </div>
          </div>

          {/* Example sentences */}
          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">Example Sentences</div>
            <div className="space-y-2">
              <EditField label="Present" value={generated.exampleSentences?.present || ''} onChange={(v) => updateField('exampleSentences.present', v)} />
              <EditField label="Past singular (with ___)" value={generated.exampleSentences?.pastSingular || ''} onChange={(v) => updateField('exampleSentences.pastSingular', v)} />
              <EditField label="Past plural (with ___)" value={generated.exampleSentences?.pastPlural || ''} onChange={(v) => updateField('exampleSentences.pastPlural', v)} />
              <EditField label="Past participle (with ___)" value={generated.exampleSentences?.pastParticiple || ''} onChange={(v) => updateField('exampleSentences.pastParticiple', v)} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EditField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="text-xs text-gray-500">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gray-200 rounded px-2 py-1 text-sm focus:ring-1 focus:ring-purple-400 focus:border-transparent"
      />
    </div>
  );
}
